/**
 * ALDER SEARCH — Contact Section
 * Design: "Alder Forest — Organic Precision"
 * Forest green background, brass accents, clean form layout
 * Split layout: left copy, right form
 * Form wired to tRPC contact.submit mutation → notifies engage@aldersearch.com
 */

import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { media } from "@/data/media";

export default function ContactSection() {
  const [formData, setFormData] = useState({
    name: "",
    firm: "",
    role: "",
    email: "",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submitMutation = trpc.contact.submit.useMutation({
    onSuccess: () => {
      setSubmitted(true);
      setError(null);
    },
    onError: (err) => {
      setError(err.message || "Something went wrong. Please try again.");
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    submitMutation.mutate({
      name: formData.name,
      email: formData.email,
      firm: formData.firm || undefined,
      role: formData.role || undefined,
      message: formData.message,
    });
  };

  const inputStyle = {
    width: "100%",
    padding: "12px 16px",
    background: "oklch(0.32 0.07 155)",
    border: "1px solid oklch(0.38 0.07 155)",
    borderRadius: "2px",
    color: "oklch(0.92 0.01 80)",
    fontFamily: "'DM Sans', system-ui, sans-serif",
    fontWeight: 400,
    fontSize: "0.9rem",
    outline: "none",
    transition: "border-color 0.2s ease",
  };

  const labelStyle = {
    display: "block",
    fontFamily: "'DM Sans', system-ui, sans-serif",
    fontWeight: 500,
    fontSize: "0.72rem",
    color: "oklch(0.65 0.02 155)",
    letterSpacing: "0.08em",
    textTransform: "uppercase" as const,
    marginBottom: "6px",
  };

  return (
    <section
      id="contact"
      className="py-28 relative overflow-hidden"
      style={{ background: "oklch(0.18 0.055 155)" }}
    >
      {/* Subtle pattern overlay */}
      <div
        className="absolute inset-0 opacity-5 pointer-events-none"
        style={{
          backgroundImage: `url(${media.abstract.webp})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />

      <div className="container relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Left: Copy */}
          <div>
            <div className="flex items-center gap-3 mb-5">
              <div
                className="h-px w-10"
                style={{ background: "oklch(0.62 0.085 75)" }}
              />
              <span
                className="font-ui text-xs tracking-widest uppercase"
                style={{
                  fontFamily: "'DM Sans', system-ui, sans-serif",
                  color: "oklch(0.72 0.09 75)",
                  letterSpacing: "0.18em",
                }}
              >
                Get In Touch
              </span>
            </div>
            <h2
              className="font-display mb-6"
              style={{
                fontFamily: "'Playfair Display', Georgia, serif",
                fontWeight: 700,
                fontSize: "clamp(2rem, 4vw, 3.2rem)",
                color: "oklch(0.952 0.012 80)",
                lineHeight: 1.15,
                letterSpacing: "-0.015em",
              }}
            >
              Start a Confidential
              <br />
              <em style={{ color: "oklch(0.72 0.09 75)", fontStyle: "italic" }}>
                Conversation
              </em>
            </h2>
            <p
              className="font-body mb-10"
              style={{
                fontFamily: "'Source Serif 4', Georgia, serif",
                fontWeight: 300,
                fontSize: "1rem",
                lineHeight: 1.8,
                color: "oklch(0.72 0.02 155)",
                maxWidth: "440px",
              }}
            >
              Book a call to talk through an open search, a deal you are preparing, or what product hires look like at your stage.
            </p>

            {/* Contact details */}
            <div className="flex flex-col gap-6">
              {[
                {
                  label: "New Business",
                  detail: "engage@aldersearch.com",
                  type: "email",
                },
              ].map((item) => (
                <div key={item.label} className="flex gap-4">
                  <div
                    className="w-px self-stretch"
                    style={{ background: "oklch(0.62 0.085 75 / 0.4)" }}
                  />
                  <div>
                    <div
                      className="font-ui mb-1"
                      style={{
                        fontFamily: "'DM Mono', monospace",
                        fontWeight: 400,
                        fontSize: "0.65rem",
                        color: "oklch(0.72 0.09 75)",
                        letterSpacing: "0.1em",
                        textTransform: "uppercase",
                      }}
                    >
                      {item.label}
                    </div>
                    <div
                      className="font-body"
                      style={{
                        fontFamily: "'Source Serif 4', Georgia, serif",
                        fontWeight: 300,
                        fontSize: "0.88rem",
                        lineHeight: 1.65,
                        color: "oklch(0.78 0.015 80)",
                        whiteSpace: "pre-line",
                      }}
                    >
                      {item.type === "email" ? (
                        <a
                          href={`mailto:${item.detail}`}
                          style={{ color: "inherit", textDecoration: "none" }}
                          onMouseEnter={(e) => {
                            (e.currentTarget as HTMLAnchorElement).style.color = "oklch(0.82 0.1 75)";
                          }}
                          onMouseLeave={(e) => {
                            (e.currentTarget as HTMLAnchorElement).style.color = "oklch(0.78 0.015 80)";
                          }}
                        >
                          {item.detail}
                        </a>
                      ) : (
                        item.detail
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Form */}
          <div
            className="p-8 lg:p-10"
            style={{
              background: "oklch(0.245 0.065 155)",
              borderRadius: "4px",
              border: "1px solid oklch(0.32 0.07 155)",
            }}
          >
            {submitted ? (
              <div className="flex flex-col items-center justify-center py-12 text-center gap-6">
                <div
                  className="w-16 h-16 rounded-full flex items-center justify-center"
                  style={{
                    background: "oklch(0.62 0.085 75 / 0.15)",
                    border: "1px solid oklch(0.62 0.085 75 / 0.4)",
                  }}
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="oklch(0.72 0.09 75)"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </div>
                <div>
                  <h3
                    className="font-display mb-2"
                    style={{
                      fontFamily: "'Playfair Display', Georgia, serif",
                      fontWeight: 600,
                      fontSize: "1.4rem",
                      color: "oklch(0.952 0.012 80)",
                    }}
                  >
                    Message Received
                  </h3>
                  <p
                    className="font-body"
                    style={{
                      fontFamily: "'Source Serif 4', Georgia, serif",
                      fontWeight: 300,
                      fontSize: "0.9rem",
                      lineHeight: 1.7,
                      color: "oklch(0.72 0.02 155)",
                    }}
                  >
                    We will be in touch within one business day.
                  </p>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="flex flex-col gap-5">
                <div>
                  <h3
                    className="font-display mb-1"
                    style={{
                      fontFamily: "'Playfair Display', Georgia, serif",
                      fontWeight: 600,
                      fontSize: "1.3rem",
                      color: "oklch(0.952 0.012 80)",
                    }}
                  >
                    Engage Alder Search
                  </h3>
                  <p
                    style={{
                      fontFamily: "'Source Serif 4', Georgia, serif",
                      fontWeight: 300,
                      fontSize: "0.82rem",
                      color: "oklch(0.65 0.02 155)",
                    }}
                  >
                    All enquiries are treated with strict confidentiality.
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label style={labelStyle}>Your Name *</label>
                    <input
                      type="text"
                      required
                      placeholder="Full name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      style={inputStyle}
                      onFocus={(e) => { (e.target as HTMLInputElement).style.borderColor = "oklch(0.62 0.085 75)"; }}
                      onBlur={(e) => { (e.target as HTMLInputElement).style.borderColor = "oklch(0.38 0.07 155)"; }}
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Firm / Company</label>
                    <input
                      type="text"
                      placeholder="Your firm"
                      value={formData.firm}
                      onChange={(e) => setFormData({ ...formData, firm: e.target.value })}
                      style={inputStyle}
                      onFocus={(e) => { (e.target as HTMLInputElement).style.borderColor = "oklch(0.62 0.085 75)"; }}
                      onBlur={(e) => { (e.target as HTMLInputElement).style.borderColor = "oklch(0.38 0.07 155)"; }}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label style={labelStyle}>Email Address *</label>
                    <input
                      type="email"
                      required
                      placeholder="your@firm.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      style={inputStyle}
                      onFocus={(e) => { (e.target as HTMLInputElement).style.borderColor = "oklch(0.62 0.085 75)"; }}
                      onBlur={(e) => { (e.target as HTMLInputElement).style.borderColor = "oklch(0.38 0.07 155)"; }}
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Role / Title</label>
                    <input
                      type="text"
                      placeholder="e.g. Managing Director"
                      value={formData.role}
                      onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                      style={inputStyle}
                      onFocus={(e) => { (e.target as HTMLInputElement).style.borderColor = "oklch(0.62 0.085 75)"; }}
                      onBlur={(e) => { (e.target as HTMLInputElement).style.borderColor = "oklch(0.38 0.07 155)"; }}
                    />
                  </div>
                </div>

                <div>
                  <label style={labelStyle}>Message *</label>
                  <textarea
                    required
                    rows={5}
                    placeholder="Briefly describe your mandate or how we can help..."
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    style={{
                      ...inputStyle,
                      resize: "vertical",
                      minHeight: "120px",
                    }}
                    onFocus={(e) => { (e.target as HTMLTextAreaElement).style.borderColor = "oklch(0.62 0.085 75)"; }}
                    onBlur={(e) => { (e.target as HTMLTextAreaElement).style.borderColor = "oklch(0.38 0.07 155)"; }}
                  />
                </div>

                {error && (
                  <p
                    style={{
                      fontFamily: "'DM Sans', system-ui, sans-serif",
                      fontSize: "0.82rem",
                      color: "oklch(0.65 0.18 25)",
                      padding: "8px 12px",
                      background: "oklch(0.65 0.18 25 / 0.1)",
                      borderRadius: "2px",
                      border: "1px solid oklch(0.65 0.18 25 / 0.3)",
                    }}
                  >
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={submitMutation.isPending}
                  className="w-full py-4 font-ui transition-all duration-300"
                  style={{
                    fontFamily: "'DM Sans', system-ui, sans-serif",
                    fontWeight: 600,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    fontSize: "0.78rem",
                    background: submitMutation.isPending
                      ? "oklch(0.52 0.065 75)"
                      : "oklch(0.62 0.085 75)",
                    color: "oklch(0.18 0.055 155)",
                    borderRadius: "2px",
                    border: "none",
                    cursor: submitMutation.isPending ? "not-allowed" : "pointer",
                  }}
                  onMouseEnter={(e) => {
                    if (!submitMutation.isPending)
                      (e.currentTarget as HTMLButtonElement).style.background = "oklch(0.72 0.09 75)";
                  }}
                  onMouseLeave={(e) => {
                    if (!submitMutation.isPending)
                      (e.currentTarget as HTMLButtonElement).style.background = "oklch(0.62 0.085 75)";
                  }}
                >
                  {submitMutation.isPending ? "Sending..." : "Send Enquiry"}
                </button>

                <p
                  style={{
                    fontFamily: "'DM Mono', monospace",
                    fontWeight: 300,
                    fontSize: "0.62rem",
                    color: "oklch(0.48 0.015 155)",
                    letterSpacing: "0.04em",
                    textAlign: "center",
                  }}
                >
                  Your details are kept strictly confidential and will only be
                  used to respond to your enquiry.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
